import { useState, useEffect, useCallback } from "react";
import { DesktopLayout } from "../../components/DesktopLayout";
import { supabase } from "../../lib/supabase";
import { BookOpen, CheckCircle2, AlertCircle, RefreshCw, Trophy } from "lucide-react";

const EMPRESA_ID = "ff438dc8-89fb-4d7c-9ea2-7e09162f242a";
const POLL_INTERVAL = 60000;

export default function TreinamentosSupervision() {
  const [treinamentos, setTreinamentos]     = useState([]);
  const [progresso, setProgresso]           = useState([]);
  const [loading, setLoading]               = useState(true);
  const [atualizando, setAtualizando]       = useState(false);
  const [ultimaAtualizacao, setUltima]      = useState(null);

  const carregar = useCallback(async (mostrarSpinner = false) => {
    if (mostrarSpinner) setAtualizando(true);

    const [resT, resP] = await Promise.all([
      supabase
        .from("liberty_treinamentos")
        .select("*")
        .eq("empresa_id", EMPRESA_ID)
        .eq("ativo", true)
        .order("ordem"),
      supabase
        .from("liberty_treinamento_progresso")
        .select("*")
        .eq("empresa_id", EMPRESA_ID)
        .eq("assistido", true),
    ]);

    if (resT.error) console.error("[Treinamentos] treinamentos:", resT.error.message);
    if (resP.error) console.error("[Treinamentos] progresso:",   resP.error.message);

    setTreinamentos(resT.data || []);
    setProgresso(resP.data   || []);
    setLoading(false);
    setUltima(new Date());
    if (mostrarSpinner) setAtualizando(false);
  }, []);

  useEffect(() => {
    carregar();
    const id = setInterval(() => carregar(), POLL_INTERVAL);
    return () => clearInterval(id);
  }, [carregar]);

  const total = treinamentos.length;

  // Group progress rows by vigilante
  const vigilantesMap = {};
  for (const p of progresso) {
    const key = p.vigilante_id || p.vigilante_nome || "unknown";
    if (!vigilantesMap[key]) {
      vigilantesMap[key] = {
        id:               p.vigilante_id,
        nome:             p.vigilante_nome || "Vigilante",
        assistidos:       new Set(),
        ultimoAssistidoEm: null,
      };
    }
    vigilantesMap[key].assistidos.add(p.treinamento_id);
    const dt = p.assistido_em ? new Date(p.assistido_em) : null;
    if (dt && (!vigilantesMap[key].ultimoAssistidoEm || dt > vigilantesMap[key].ultimoAssistidoEm)) {
      vigilantesMap[key].ultimoAssistidoEm = dt;
    }
  }

  const vigilantesRelatorio = Object.values(vigilantesMap).sort(
    (a, b) => b.assistidos.size - a.assistidos.size
  );
  const totalCompletos = vigilantesRelatorio.filter(
    (v) => total > 0 && v.assistidos.size >= total
  ).length;

  const fmtDH = (dt) =>
    dt
      ? dt.toLocaleString("pt-BR", {
          day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
        })
      : "—";

  return (
    <DesktopLayout
      title="Treinamentos"
      subtitle="Acompanhamento de capacitação da equipe de vigilância"
      actions={
        <div className="flex items-center gap-3">
          {ultimaAtualizacao && (
            <p className="text-[11px] text-slate-500 font-mono-data hidden xl:block">
              Atualizado às{" "}
              {ultimaAtualizacao.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
            </p>
          )}
          <button
            onClick={() => carregar(true)}
            disabled={atualizando}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-slate-300 text-xs font-medium hover:bg-white/10 transition disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${atualizando ? "animate-spin" : ""}`} />
            Atualizar
          </button>
        </div>
      }
    >
      {/* KPI cards */}
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="rounded-2xl bg-[#0a1128] border border-white/10 p-5">
          <p className="tiny-label text-slate-500">Vídeos no programa</p>
          <p className="text-3xl font-bold mt-4">{loading ? "…" : total}</p>
          <p className="text-xs text-slate-400 mt-1">treinamentos disponíveis</p>
        </div>
        <div className="rounded-2xl bg-[#0a1128] border border-white/10 p-5">
          <p className="tiny-label text-slate-500">Vigilantes treinando</p>
          <p className="text-3xl font-bold mt-4">{loading ? "…" : vigilantesRelatorio.length}</p>
          <p className="text-xs text-slate-400 mt-1">com progresso registrado</p>
        </div>
        <div className="rounded-2xl bg-[#0a1128] border border-emerald-500/30 p-5">
          <p className="tiny-label text-emerald-500">Treinamentos completos</p>
          <p className="text-3xl font-bold mt-4 text-emerald-400">{loading ? "…" : totalCompletos}</p>
          <p className="text-xs text-slate-400 mt-1">vigilantes 100% capacitados</p>
        </div>
      </div>

      {/* Conteúdo do programa */}
      <div className="rounded-2xl bg-[#0a1128] border border-white/10 overflow-hidden mb-6">
        <div className="p-5 border-b border-white/10">
          <p className="tiny-label text-slate-500">Conteúdo</p>
          <h2 className="text-xl font-bold mt-1">Vídeos do programa ({total})</h2>
        </div>
        {loading ? (
          <div className="p-5 text-center text-sm text-slate-500 italic">Carregando…</div>
        ) : treinamentos.length === 0 ? (
          <div className="p-10 text-center">
            <BookOpen className="w-8 h-8 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">Nenhum vídeo cadastrado ainda.</p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {treinamentos.map((t) => {
              const totalAssistiu = progresso.filter((p) => p.treinamento_id === t.id).length;
              return (
                <div key={t.id} className="px-5 py-3.5 flex items-center gap-4">
                  <div className="w-8 h-8 rounded-lg bg-blue-500/20 border border-blue-500/30 flex items-center justify-center text-xs font-bold text-blue-300 shrink-0">
                    {t.ordem ?? "—"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{t.titulo}</p>
                    {t.descricao && (
                      <p className="text-xs text-slate-500 truncate mt-0.5">{t.descricao}</p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-white">{totalAssistiu}</p>
                    <p className="text-[11px] text-slate-500">vigilante(s)</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Progresso por vigilante */}
      <div className="rounded-2xl bg-[#0a1128] border border-white/10 overflow-hidden">
        <div className="p-5 border-b border-white/10">
          <p className="tiny-label text-slate-500">Acompanhamento individual</p>
          <h2 className="text-xl font-bold mt-1">Status por vigilante</h2>
        </div>

        {loading ? (
          <div className="p-5 text-center text-sm text-slate-500 italic">Carregando…</div>
        ) : vigilantesRelatorio.length === 0 ? (
          <div className="p-10 text-center">
            <BookOpen className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="font-semibold text-slate-400">Nenhum progresso registrado</p>
            <p className="text-sm text-slate-500 mt-1">
              Os vigilantes ainda não assistiram nenhum vídeo.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 bg-white/5">
                  <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Vigilante
                  </th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Progresso
                  </th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Último vídeo
                  </th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {vigilantesRelatorio.map((v) => {
                  const concluido = total > 0 && v.assistidos.size >= total;
                  const pct       = total > 0 ? Math.round((v.assistidos.size / total) * 100) : 0;
                  return (
                    <tr key={v.id || v.nome} className="hover:bg-white/[0.03]">
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-xs font-bold text-white shrink-0">
                            {v.nome
                              .split(" ")
                              .map((n) => n[0])
                              .slice(0, 2)
                              .join("")
                              .toUpperCase()}
                          </div>
                          <span className="font-semibold text-white">{v.nome}</span>
                          {concluido && (
                            <Trophy className="w-4 h-4 text-emerald-400" />
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-28 h-1.5 bg-white/10 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${
                                concluido ? "bg-emerald-500" : "bg-blue-500"
                              }`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className="text-xs text-slate-300 font-mono-data whitespace-nowrap">
                            {v.assistidos.size}/{total} ({pct}%)
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-xs text-slate-400 font-mono-data">
                        {fmtDH(v.ultimoAssistidoEm)}
                      </td>
                      <td className="px-5 py-3.5">
                        {concluido ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-[11px] font-semibold">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Completo ✓
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/20 text-amber-300 text-[11px] font-semibold">
                            <AlertCircle className="w-3.5 h-3.5" /> Pendente ⚠
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DesktopLayout>
  );
}
